// Generado desde design-system/components/feedback/Skeleton.tsx — no editar; edita el .ts/.tsx y corre build-ds.
import { cn } from '../../lib/cn';
import './feedback-styles';
const RADIUS = { sm: 'rounded-xs', md: 'rounded-sm', lg: 'rounded-md', full: 'rounded-full' };
export function Skeleton({ className, radius = 'sm', delay = 0, style }) {
    return (<span aria-hidden="true" className={cn('dbf-skel block', RADIUS[radius] ?? RADIUS.sm, className)} style={{ ...style, '--dbf-delay': `${delay}ms` }}/>);
}
/** Contenedor accesible: anuncia una vez y marca aria-busy. */
export function SkeletonGroup({ label = 'Cargando…', className, children, as }) {
    const Tag = (as ?? 'div');
    return (<Tag role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </Tag>);
}
/** Líneas de texto; la última más corta, como un párrafo real. */
export function SkeletonText({ lines = 3, className, lineClassName = 'h-3' }) {
    const widths = ['w-full', 'w-11/12', 'w-4/5', 'w-10/12', 'w-3/5'];
    return (<span className={cn('flex flex-col gap-2.5', className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (<Skeleton key={i} className={cn(lineClassName, i === lines - 1 ? 'w-3/5' : widths[i % widths.length])} radius="full" delay={i * 60}/>))}
    </span>);
}
/** Tarjeta de SKU / módulo: miniatura + título + dos líneas + pie con chip. */
export function SkeletonCard({ className, label = 'Cargando tarjeta…' }) {
    return (<SkeletonGroup label={label} className={cn('db-glass flex flex-col gap-4 p-5', className)}>
      <div className="flex items-center gap-3">
        <Skeleton className="h-11 w-11" radius="md"/>
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-3/5" radius="full" delay={40}/>
          <Skeleton className="h-3 w-2/5" radius="full" delay={80}/>
        </div>
      </div>
      <SkeletonText lines={2}/>
      <div className="flex items-center justify-between pt-1">
        <Skeleton className="h-6 w-24" radius="full" delay={120}/>
        <Skeleton className="h-9 w-9" radius="full" delay={160}/>
      </div>
    </SkeletonGroup>);
}
const COLS = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' };
/** Fila de KPIs: etiqueta, cifra grande (font-display), delta. */
export function SkeletonKpiRow({ count = 4, className, label = 'Cargando indicadores…' }) {
    return (<SkeletonGroup label={label} className={cn('grid grid-cols-1 gap-4', COLS[count] ?? COLS[4], className)}>
      {Array.from({ length: count }, (_, i) => (<div key={i} className="db-glass flex flex-col gap-3 p-5" aria-hidden="true">
          <Skeleton className="h-3 w-24" radius="full" delay={i * 70}/>
          <Skeleton className="h-9 w-32" radius="sm" delay={i * 70 + 40}/>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-14" radius="full" delay={i * 70 + 80}/>
            <Skeleton className="h-3 w-20" radius="full" delay={i * 70 + 100}/>
          </div>
        </div>))}
    </SkeletonGroup>);
}
/** Tabla: encabezado + filas con las mismas columnas que la tabla real. */
export function SkeletonTable({ rows = 5, columns = ['w-2/5', 'w-1/6', 'w-1/6', 'w-1/6'], className, label = 'Cargando tabla…' }) {
    return (<SkeletonGroup label={label} className={cn('db-glass overflow-hidden p-0', className)}>
      <div className="flex items-center gap-6 border-b border-hair px-5 py-3.5" aria-hidden="true">
        {columns.map((_w, c) => (<span key={c} className={cn('flex', c === 0 ? 'flex-[2.4]' : 'flex-1 justify-end')}>
            <Skeleton className={cn('h-2.5 w-16')} radius="full"/>
          </span>))}
      </div>
      {Array.from({ length: rows }, (_, r) => (<div key={r} className="flex items-center gap-6 border-b border-hair px-5 py-3.5 last:border-0" aria-hidden="true">
          {columns.map((_w, c) => (<span key={c} className={cn('flex items-center gap-3', c === 0 ? 'flex-[2.4]' : 'flex-1 justify-end')}>
              {c === 0 && <Skeleton className="h-8 w-8 shrink-0" radius="sm" delay={r * 50}/>}
              <Skeleton className={cn('h-3', c === 0 ? 'w-full max-w-[220px]' : 'w-full max-w-[72px]')} radius="full" delay={r * 50 + c * 30}/>
            </span>))}
        </div>))}
    </SkeletonGroup>);
}
/** Gráfica: título, leyenda y barras con alturas variadas sobre una base. */
export function SkeletonChart({ bars = [42, 64, 38, 78, 56, 88, 61, 72, 49, 83, 67, 92], className, label = 'Cargando gráfica…' }) {
    return (<SkeletonGroup label={label} className={cn('db-glass flex flex-col gap-5 p-5', className)}>
      <div className="flex items-start justify-between gap-4" aria-hidden="true">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-44" radius="full"/>
          <Skeleton className="h-3 w-28" radius="full" delay={60}/>
        </div>
        <div className="flex gap-3">
          <Skeleton className="h-3 w-16" radius="full" delay={90}/>
          <Skeleton className="h-3 w-16" radius="full" delay={120}/>
        </div>
      </div>
      <div className="relative flex h-40 items-end gap-2 border-b border-hair-2 pb-px" aria-hidden="true">
        {[0.25, 0.5, 0.75].map((g) => (<span key={g} className="absolute inset-x-0 border-t border-dashed border-hair" style={{ bottom: `${g * 100}%` }}/>))}
        {bars.map((b, i) => (<Skeleton key={i} className="relative flex-1" radius="sm" delay={i * 40} style={{ height: `${b}%`, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}/>))}
      </div>
    </SkeletonGroup>);
}
